<template lang="pug">
.analytics__queue
    v-container(fluid)
        v-card(light)
            v-card-title Analytics Task Queue
            v-card-text
                v-row(align="center")
                    v-col(cols="auto")
                        v-text-field(v-model="search" label="Search" clearable color='brand')
                    v-col(cols="auto")
                        v-btn(
                            @click="fetchQueue"
                            color="brand"
                            small
                            class="white--text"
                            :disabled="isRefreshing || isAutoRefresh"
                        ) Refresh
                    v-col(cols="auto")
                        v-tooltip(top)
                            template(v-slot:activator="{ on, attrs }")
                                v-btn(
                                    v-bind="attrs"
                                    v-on="on"
                                    :color="isAutoRefresh ? 'red' : 'green'"
                                    small
                                    class="white--text"
                                    @click="toggleAutoRefresh"
                                )
                                    | {{ isAutoRefresh ? 'Stop Auto-Refresh' : 'Start Auto-Refresh' }}
                            span The refresh rate is 30 seconds
                    v-col(cols="auto")
                        v-menu(
                            ref="menu"
                            v-model="menu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                            color="brand"
                        )
                            template(v-slot:activator="{ on, attrs }")
                                v-text-field(
                                    v-model="refreshStartDate"
                                    label="Select Start Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                )
                            v-date-picker(
                                light
                                v-model="refreshStartDate"
                                @input="menu = false"
                                color="brand"
                                elevation="3"
                                show-current
                                header-color="brand"
                                :max="new Date().toISOString().split('T')[0]"
                                :show-week="true"
                            )
                    v-col(cols="auto")
                        span(class="last-refreshed" style="margin-left: 10px; font-size: 0.9em;")
                            | Last Refreshed: {{ lastRefreshed }}
                v-data-table(
                    :headers="headers"
                    :items="queueItems"
                    :search="search"
                    :items-per-page="5"
                    sort-by="id"
                    sort-desc = true
                    item-key="id"
                )
                    template(v-slot:item.system_id="{ item }")
                        span.system-id-column {{ item.system_id }}

                    template(v-slot:item.id="{ item }")
                        span {{ item.id }}
                            v-icon(small @click="showHistory(item)") history
                            v-icon(
                                @click="editTask(item)"
                                color="blue"
                                small
                                class="ml-2"
                                disabled
                            ) edit

                    template(v-slot:item.status="{ item }")
                        v-chip(:color="getStatusColor(item.status)" dark) {{ item.status }}
                    template(v-slot:item.createdAt="{ item }")
                        div
                            | {{ formatDate(item.createdAt, 'date', 'main') }}
                            br
                            | {{ formatDate(item.createdAt, 'time', 'main') }}
                    template(#item.updatedAt="{ item }")
                        div
                            | {{ formatDate(item.updatedAt, 'date', 'main') }}
                            br
                            | {{ formatDate(item.updatedAt, 'time', 'main') }}
                    template(v-slot:item.message="{ item }")
                        span(style="font-size: 0.8em;") {{ item.message }}

                    template(v-slot:item.error="{ item }")
                        v-tooltip(bottom)
                            template(v-slot:activator="{ on, attrs }")
                                span(
                                    style="font-size: 0.8em; cursor: pointer; text-decoration: underline dotted;"
                                    v-bind="attrs"
                                    v-on="on"
                                ) {{ truncateText(item.error, 10) }}
                            span {{ item.error }}

                    template(v-slot:item.user="{ item }")
                        span(style="font-size: 0.8em;") {{ formatUserEmail(item.user) }}

    v-dialog(v-model="historyDialog" max-width="90%" )
        v-card(light dense)
            v-card-title Task History for Q ID: {{ selectedTaskInfo.queueId  }}
            v-card-subtitle {{ selectedTaskInfo.taskName }} - {{ selectedTaskInfo.systemId }}
            v-card-text
                v-data-table(
                    :headers="historyHeaders"
                    :items="selectedTaskHistory"
                    item-key="id"
                    dense
                )

                    template(v-slot:item.error="{ item }")
                        v-tooltip(bottom)
                            template(v-slot:activator="{ on, attrs }")
                                span(
                                    style="font-size: 0.8em; cursor: pointer; text-decoration: underline dotted;"
                                    v-bind="attrs"
                                    v-on="on"
                                ) {{ truncateText(item.error, 10) }}
                            span {{ item.error }}

                    template(v-slot:item.user="{ item }")
                        span(style="font-size: 0.8em;") {{ formatUserEmail(item.user) }}

            v-card-actions
                v-spacer
                v-btn(color="brand" text @click="historyDialog = false", class="white--text") Close

    v-dialog(v-model="editDialog" max-width="500px")
        v-card(light)
            v-card-title Edit Task
            v-card-subtitle
                h3 QID: {{ taskToEdit?.id }} - {{ taskToEdit?.taskInfo?.task_name}}  ({{ taskToEdit?.system_id }})
            v-card-text
                h4 Are you sure you want to edit this queue item?
                v-row
                    v-col(cols="auto")
                        v-select(
                            v-model="taskToEdit.status"
                            label="Task Status"
                            color='brand'
                            :items="statusItems"
                        )

            v-card-actions
                v-spacer
                v-btn(color="red" text @click="confirmEdit") Confirm
                v-btn(color="grey" text @click="editDialog = false") Cancel

</template>

<script>

import {DateTime} from 'luxon';
export default {
    meta: {
        role: 'analytics-queue'
    },
    emits: [],
    data () {
        return {
            search: '',
            headers: [
                { text: 'Q ID', value: 'id', filterable: false },
                { text: 'Task ID', value: 'task_id'},
                { text: 'System ID', value: 'system_id'},
                { text: 'Name', value: 'taskInfo.task_name' },
                { text: 'Status', value: 'status' },
                { text: 'Attempts', value: 'attempts_count', sortable: false, align: 'center' },
                { text: 'Created At (Svr)', value: 'createdAt', sortable: false, filterable: false  },
                { text: 'Updated At (Svr)', value: 'updatedAt', sortable: false, filterable: false  },
                { text: 'Message', value: 'message', sortable: false  },
                { text: 'Error', value: 'error', sortable: false  },
                { text: 'User', value: 'user', sortable: false },
            ],
            historyHeaders: [
                { text: 'Changed (Sys)', value: 'createdAtFormatted', sortable: false },
                { text: 'Prior', value: 'old_status', sortable: false },
                { text: 'New', value: 'new_status', sortable: false },
                { text: 'Message', value: 'message', sortable: false },
                { text: 'Error', value: 'error',sortable: false },
                { text: 'User', value: 'user',sortable: false }
            ],
            queueItems: [],
            error: '',
            isAutoRefresh: false, // Flag to track auto-refresh state
            autoRefreshInterval: null, // Interval ID for auto-refresh
            historyDialog: false, // Dialog visibility state
            selectedTaskHistory: [], // History of the selected task
            selectedTaskInfo: {
                taskName: '',
                systemId: '',
                queueId: null,
            },
            deleteDialog: false,
            taskToEdit: {},
            editDialog: false,
            allPossibleStatuses: [
                'PENDING',
                'COMPLETED',
                'FAILED',
                'EXPIRED',
                'WAITING',
                'REPLACED'
            ],
            menu: false,
            refreshStartDate: DateTime.now().minus({ days: 1 }).toISODate(),
            lastRefreshed: DateTime.now().toFormat('MM/dd hh:mm:ss a ZZZZ'),
            isRefreshing: false,
        };
    },
    methods: {
        fetchQueue () {
            this.isRefreshing = true;
            this.socketEmit('analyticsTaskQueue', {startDate: this.refreshStartDate}, ({queue, error}) => {
                if(error) {
                    this.$set(this, 'error', error);
                    console.error('Error fetching queue items:', error);
                } else {
                    this.$set(this, 'queueItems', queue);
                    this.lastRefreshed = DateTime.now().toFormat('M/dd hh:mm:ss a ZZZZ');
                    const refreshedElement = this.$el.querySelector('.last-refreshed');
                    if (refreshedElement) {
                        refreshedElement.classList.add('refresh-highlight');
                        setTimeout(() => {
                            refreshedElement.classList.remove('refresh-highlight');
                        }, 1000);
                    }
                }
                this.isRefreshing = false;
            });
        },

        editTask (task){
            this.taskToEdit = task;
            this.editDialog = true;
            console.log('edit task', task);
        },

        confirmEdit () {
            // Logic to edit the task
        },

        resetTask (task){
            console.log('reset task', task);
        },

        toggleAutoRefresh () {
            if (this.isAutoRefresh) {
                // Stop auto-refresh
                clearInterval(this.autoRefreshInterval);
                this.autoRefreshInterval = null;
                this.isAutoRefresh = false;
            } else {
                // Start auto-refresh
                this.autoRefreshInterval = setInterval(this.fetchQueue, 30000);
                this.isAutoRefresh = true;
            }
        },
        getStatusColor (status) {
            const colors = {
                PENDING: 'grey',
                IN_PROGRESS: 'blue',
                COMPLETED: 'green',
                FAILED: 'red',
                EXPIRED: 'orange',
                WAITING: 'teal',
                REPLACED: 'purple',
            };
            return colors[status] || 'grey';
        },
        formatDate (dateString, type) {
            if (!dateString) return '';
            if (type === 'date') {
                return DateTime.fromISO(dateString).toFormat('M/dd');
            }
            if (type === 'time') {
                return DateTime.fromISO(dateString).toFormat('H:mm:ss');
            }
            return '';
        },

        formatUserEmail(email) {
            if (!email) return '';
            return email.split('@')[0];
        },

        truncateText(text, maxLength = 20) {
            if (!text) return '';
            if (text.length <= maxLength) return text;
            return text.slice(0, maxLength) + '...';
        },

        formatTaskHistory (history) {
            return history.map(task => ({
                ...task,
                createdAtFormatted: task.createdAt
                    ? DateTime.fromISO(task.createdAt).toLocal().toFormat('MM/dd hh:mm:ss a ZZZZ')
                    : '',
            }));
        },

        showHistory (item){
            this.selectedTaskInfo.taskName = item.taskInfo?.task_name || 'Unknown';
            this.selectedTaskInfo.systemId = item.system_id;
            this.selectedTaskInfo.queueId = item.id;
            this.selectedTaskHistory = this.formatTaskHistory(item.queueStatusHistory) || [];
            this.historyDialog = true;
        }
    },
    computed: {
        statusItems () {
            if(this.taskToEdit?.status === 'WAITING') {
                return [];
            }
            return this.allPossibleStatuses.filter(s => s !== this.taskToEdit?.status && s !== 'WAITING');
        }
    },
    watch: {
        refreshStartDate (newVal) {
            if (newVal) {
                this.fetchQueue();
            }
        }
    },
    mounted () {
        this.fetchQueue();
    }
};
</script>

<style scoped>
.system-id-column {
  font-family: 'Times New Roman', 'Baskerville', serif;
}
.refresh-highlight {
  background-color: yellow;
  transition: background-color 1s ease-out;
}
.v-container {
    max-width: 900px;
}
</style>
